import { jwtDecode } from "jwt-decode";
import type { PropsWithChildren } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { queryClient } from "$src/core/contexts/query";
import useApi from "$src/core/hooks/use-api";
import type { ProfileDto, AccountDto } from "$src/core/models/dto";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import useAccountQuery from "$src/core/queries/use-account-query";
import useProfilesQuery from "$src/core/queries/use-profiles-query";
import Logging from "$src/core/services/logging/logging";
import SecureStorage from "$src/core/services/secure-storage";
import Storage from "$src/core/services/storage/storage";
import hasValidRefreshToken from "$src/core/utils/auth/has-valid-refresh-token";
import isTokenValid from "$src/core/utils/auth/is-token-valid";
import isHttpError from "$src/core/utils/error/is-http-error";
import isNetworkError from "$src/core/utils/error/is-network-error";

type AuthStatus = "authenticated" | "unauthenticated";

export type AuthContextType = {
  suspense: boolean;
  status: AuthStatus | null;
  account: AccountDto | null;
  profile: ProfileDto | null;
  profileId: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    details?: { firstName?: string; lastName?: string },
  ) => Promise<void>;
  startPairing: () => Promise<{ code: string; expiresAt: number } | null>;
  authorizePairing: (code: string) => Promise<boolean>;
  checkPairing: (pairingCode: string) => Promise<boolean>;
  logout: () => Promise<void>;
  selectProfile: (profile: string) => void;
  deselectProfile: () => void;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { api } = useApi();

  const [profileId, setProfileId] = useState<string | null>(() =>
    Storage.get("auth.profileId"),
  );
  const [status, setStatus] = useState<AuthStatus | null>(null);

  const refreshCountRef = useRef(0);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  const refreshScheduleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: account,
    status: accountStatus,
    error: accountError,
  } = useAccountQuery({
    enabled: status === "authenticated",
  });
  const {
    data: profiles,
    status: profilesStatus,
    error: profilesError,
  } = useProfilesQuery({
    enabled: accountStatus === "success" && status === "authenticated",
  });

  const profile = profiles?.find((p) => p.id === profileId) ?? null;

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await api.login(email, password);
        if (!response.accessToken) {
          throw Error("No access token in response");
        }
        if (!response.refreshToken) {
          throw Error("No refresh token in response");
        }

        SecureStorage.set("auth.accessToken", response.accessToken);
        SecureStorage.set("auth.refreshToken", response.refreshToken);

        if (response.userId) {
          SecureStorage.set("auth.userId", response.userId.toString());
        }

        setStatus("authenticated");
        setProfileId(null);
      } catch (error) {
        setStatus("unauthenticated");
        setProfileId(null);
        Logging.error("Could not login", { error });
        throw error;
      }
    },
    [setProfileId, api],
  );

  const register = useCallback(
    async (
      email: string,
      password: string,
      details?: { firstName?: string; lastName?: string },
    ) => {
      if (!api.register) {
        throw Error("Registration is not available");
      }

      try {
        const response = await api.register(email, password, details);
        if (!response.accessToken) {
          throw Error("No access token in response");
        }
        if (!response.refreshToken) {
          throw Error("No refresh token in response");
        }

        SecureStorage.set("auth.accessToken", response.accessToken);
        SecureStorage.set("auth.refreshToken", response.refreshToken);

        if (response.userId) {
          SecureStorage.set("auth.userId", response.userId.toString());
        }

        setStatus("authenticated");
        setProfileId(null);
      } catch (error) {
        setStatus("unauthenticated");
        setProfileId(null);
        Logging.error("Could not register", { error });
        throw error;
      }
    },
    [setProfileId, api],
  );

  const logout = useCallback(async () => {
    try {
      await Promise.all([
        SecureStorage.remove("auth.accessToken"),
        SecureStorage.remove("auth.refreshToken"),
        SecureStorage.remove("auth.userId"),
      ]);
    } catch (error) {
      Logging.error("Could not logout, error removing tokens", { error });
      throw error;
    } finally {
      setStatus("unauthenticated");
      setProfileId(null);
      Storage.remove("auth.profileId");
      Storage.remove("profiles");
      queryClient.clear();
    }
  }, [setProfileId, setStatus]);

  const refresh = useCallback(async () => {
    // If a refresh is already in progress, just wait for that one to complete
    // This prevents multiple refresh requests from being sent at the same time
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const execute = async () => {
      try {
        if (refreshCountRef.current >= 3) {
          throw Error("Too many refresh attempts");
        }

        refreshCountRef.current++;

        if (!(await hasValidRefreshToken())) {
          throw Error("Cannot refresh, no valid refresh token available");
        }

        const refreshToken = SecureStorage.get("auth.refreshToken");
        if (!refreshToken) {
          throw Error("No refresh token found");
        }

        const response = await api.refresh(refreshToken);
        if (!response.accessToken) {
          throw Error("No access token in response");
        }
        if (!response.refreshToken) {
          throw Error("No refresh token in response");
        }

        SecureStorage.set("auth.accessToken", response.accessToken);
        SecureStorage.set("auth.refreshToken", response.refreshToken);

        if (response.userId) {
          SecureStorage.set("auth.userId", response.userId.toString());
        }

        if (refreshScheduleTimeoutRef.current) {
          clearTimeout(refreshScheduleTimeoutRef.current);
        }

        const { iat = Date.now() / 1000, exp } = jwtDecode(
          response.accessToken,
        );

        // Schedule a refresh well before the token expires
        if (exp) {
          const lifetime = (exp - iat) * 1000;
          const timeUntilExpiry = Math.max(0, exp * 1000 - Date.now());

          // Half of the token's lifetime
          const timeToSchedule = Math.max(0, timeUntilExpiry - lifetime / 2);

          refreshScheduleTimeoutRef.current = setTimeout(
            refresh,
            timeToSchedule,
          );
        }

        return true;
      } catch (error) {
        Logging.warn("Could not refresh", { error });

        // Logout if the refresh token is invalid/expired, ignoring network
        // errors since we might be offline and want to retry later
        if (
          !isNetworkError(error) &&
          isHttpError(error) &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          await logout();
        }
        // Also logout if we can tell ourself that the refresh token is invalid/expired
        else if (!(await hasValidRefreshToken())) {
          await logout();
        }

        return false;
      } finally {
        refreshCountRef.current = 0;
      }
    };

    try {
      const promise = execute();
      refreshPromiseRef.current = promise;
      return await promise;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [api, logout]);

  const startPairing = useCallback(async () => {
    const response = await api.generatePairingCode();
    if (!response.code) {
      throw Error("No code in response");
    }
    if (!response.expiresAt) {
      throw Error("No expiresAt in response");
    }

    return {
      code: response.code,
      expiresAt: response.expiresAt,
    };
  }, [api]);

  const authorizePairing = useCallback(
    async (code: string) => api.authorizePairingCode(code),
    [api],
  );

  const checkPairing = useCallback(
    async (pairingCode: string) => {
      try {
        const response = await api.checkPairingCode(pairingCode);
        if (!response.accessToken) {
          throw Error("No access token in response");
        }
        if (!response.refreshToken) {
          throw Error("No refresh token in response");
        }

        SecureStorage.set("auth.accessToken", response.accessToken);
        SecureStorage.set("auth.refreshToken", response.refreshToken);

        if (response.userId) {
          SecureStorage.set("auth.userId", response.userId.toString());
        }

        setStatus("authenticated");
        return true;
      } catch {
        return false;
      }
    },
    [api],
  );

  const selectProfile = useCallback((id: string) => {
    setProfileId(id);
    Storage.set("auth.profileId", id);
  }, []);

  const deselectProfile = useCallback(() => {
    setProfileId(null);
    Storage.remove("auth.profileId");
  }, []);

  /* Handle account query errors */
  useEffect(() => {
    if (status === "authenticated" && accountStatus === "error") {
      Logging.error("Could not get account", { error: accountError });
    }
  }, [accountStatus, status, accountError]);

  /* Handle profiles query errors */
  useEffect(() => {
    if (status === "authenticated" && profilesStatus === "error") {
      Logging.error("Could not get profiles", { error: profilesError });
    }
  }, [profilesStatus, status, profilesError]);

  /* Initialize the authentication status */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const accessToken = SecureStorage.get("auth.accessToken");
        const refreshToken = SecureStorage.get("auth.refreshToken");

        if (accessToken && isTokenValid(accessToken)) {
          setStatus("authenticated");

          const { iat, exp } = jwtDecode(accessToken);

          // Schedule a refresh well before the token expires
          if (exp) {
            const lifetime = iat ? (exp - iat) * 1000 : undefined;
            const timeUntilExpiry = Math.max(0, exp * 1000 - Date.now());

            // Half of the token's lifetime, or otherwise half of the time until expiry
            const timeToSchedule = Math.max(
              0,
              timeUntilExpiry - (lifetime ? lifetime / 2 : timeUntilExpiry / 2),
            );

            refreshScheduleTimeoutRef.current = setTimeout(
              refresh,
              timeToSchedule,
            );
          }
        } else if (refreshToken) {
          const refreshed = await refresh();
          setStatus(refreshed ? "authenticated" : "unauthenticated");
        } else {
          setStatus("unauthenticated");
        }
      } catch (error) {
        Logging.error("Could not initialize auth", { error });
        await logout();
        setStatus("unauthenticated");
      }
    };
    void initializeAuth();
  }, [setStatus, refresh, logout]);

  /* Cleanup the refresh timeout on unmount */
  useEffect(
    () => () => {
      if (refreshScheduleTimeoutRef.current != null) {
        clearTimeout(refreshScheduleTimeoutRef.current);
        refreshScheduleTimeoutRef.current = null;
      }
    },
    [],
  );

  /* Capture 401 errors and refresh access token and then retry request when possible */
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    if (api.onPrivateResponseInterceptor) {
      cleanup = api.onPrivateResponseInterceptor(async (error, retry) => {
        if (error.status === 401 && !error._retry) {
          const refreshed = await refresh().catch(() => false);
          if (refreshed) return retry();
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          return Promise.reject(error);
        }
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(error);
      });
    }
    return () => cleanup?.();
  }, [api, logout, refresh]);

  const suspense = useMemo(
    () =>
      !status ||
      (status === "authenticated" &&
        (accountStatus === "pending" || profilesStatus === "pending")),
    [accountStatus, profilesStatus, status],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      suspense,
      status,
      account: account ?? null,
      profile,
      profileId,
      login,
      register,
      logout,
      selectProfile,
      deselectProfile,
      startPairing,
      authorizePairing,
      checkPairing,
    }),
    [
      suspense,
      status,
      account,
      profile,
      profileId,
      login,
      register,
      logout,
      selectProfile,
      deselectProfile,
      startPairing,
      authorizePairing,
      checkPairing,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const withAuthProvider =
  <T extends PropsWithChildren>(Component: React.ComponentType<T>) =>
  (props: T) => (
    <AuthProvider>
      <Component {...props} />
    </AuthProvider>
  );

const SuspensedAuthProvider = withAuthProvider(
  ({ children }: PropsWithChildren) => {
    const context = useContext(AuthContext);

    if (!context) {
      return <LoadingPage />;
    }

    return children;
  },
);

export default SuspensedAuthProvider;
